import { useState } from 'react';

// Signal-Blue (#2997ff / $accent) keyboard focus-ring geometry, shared so every
// in-scope control renders an identical ring. Mirrors the state-driven pattern
// already used by the VehicleActionsMenu trigger.
const FOCUS_RING_WIDTH_PX = 2;
const FOCUS_RING_OFFSET_PX = 2;

/**
 * Tamagui `focusVisibleStyle` value that draws the accent ring on keyboard focus.
 * Deterministic (a CSS `:focus-visible` rule) rather than React state, so it is
 * reliable for controls the state-driven hook cannot cover — e.g. an
 * `aria-disabled` menu item — and for overriding Tamagui `Button`'s built-in
 * `$outlineColor` focus ring.
 */
export const ACCENT_FOCUS_VISIBLE_STYLE = {
  outlineColor: '$accent',
  outlineStyle: 'solid',
  outlineWidth: FOCUS_RING_WIDTH_PX,
  outlineOffset: FOCUS_RING_OFFSET_PX,
} as const;

/** Outline props + focus/blur handlers to spread onto a state-driven pressable. */
interface FocusRingProps {
  outlineColor: '$accent';
  outlineStyle: 'solid' | 'none';
  outlineWidth: number;
  outlineOffset: number;
  onFocus: () => void;
  onBlur: () => void;
}

/**
 * State-driven Signal-Blue focus ring for the custom `tag="button"` pressables
 * (account-menu trigger + items, theme toggle, notifications bell, vehicle
 * action items). Toggling the base outline on focus/blur makes the ring visible
 * under both keyboard and programmatic focus, mirroring the proven
 * VehicleActionsMenu trigger. Spread the result onto the pressable.
 */
export function useFocusRing(): FocusRingProps {
  const [focused, setFocused] = useState(false);
  return {
    outlineColor: '$accent',
    outlineStyle: focused ? 'solid' : 'none',
    outlineWidth: focused ? FOCUS_RING_WIDTH_PX : 0,
    outlineOffset: FOCUS_RING_OFFSET_PX,
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
  };
}
